import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/hooks/use-organization";
import { useSubscription, tierHasFeature } from "@/hooks/use-subscription";

// Available feature flags
export type FeatureFlag =
  | "google_sheets_sync"
  | "earnings"
  | "pulse_check"
  | "workouts"
  | "ai_assistant"
  | "kroger_integration"
  | "messages"
  | "sms"
  | "client_portal"
  | "google_calendar"
  | "import"
  | "marketing"
  | "route_intelligence"
  | "dynamic_pricing"
  | "marketplace"
  | "self_service_packages"
  | "multi_location"
  | "financial_intelligence"
  | "health_hub"
  | "bulk_messaging";

export const FEATURE_FLAG_CONFIG: Record<FeatureFlag, { label: string; description: string }> = {
  google_sheets_sync: {
    label: "Google Sheets Sync",
    description: "Automatically log gym tour and consult conversions",
  },
  earnings: {
    label: "Earnings",
    description: "Track revenue from packages and sessions",
  },
  pulse_check: {
    label: "Pulse Check",
    description: "Analytics and insights dashboard",
  },
  workouts: {
    label: "Workouts",
    description: "Workout planning and tracking system",
  },
  ai_assistant: {
    label: "AI Assistant",
    description: "Chat-based assistant for scheduling and client management",
  },
  kroger_integration: {
    label: "Kroger Integration",
    description: "Connect to Kroger for one-tap grocery ordering from meal plans",
  },
  messages: {
    label: "Messages",
    description: "In-app messaging between trainers and clients",
  },
  sms: {
    label: "SMS Messaging",
    description: "Send text messages to clients via Twilio",
  },
  client_portal: {
    label: "Client Portal",
    description: "Client-facing dashboard for booking, progress, and workouts",
  },
  google_calendar: {
    label: "Google Calendar",
    description: "Sync sessions with Google Calendar",
  },
  import: {
    label: "Data Import",
    description: "Import clients and sessions from spreadsheets",
  },
  marketing: {
    label: "Marketing Suite",
    description: "AI-powered marketing tools including content studio, email campaigns, automations, and ROI tracking",
  },
  route_intelligence: {
    label: "Route Intelligence",
    description: "Route optimization and location analytics for mobile trainers",
  },
  dynamic_pricing: {
    label: "Dynamic Pricing",
    description: "Time-based pricing with peak and off-peak rates",
  },
  marketplace: {
    label: "Marketplace",
    description: "Trainer-client matching engine and marketplace discovery",
  },
  self_service_packages: {
    label: "Self-Service Packages",
    description: "Let clients browse and purchase training packages from the portal",
  },
  multi_location: {
    label: "Multi-Location Management",
    description: "Manage multiple gym locations with per-location analytics and staff assignment",
  },
  financial_intelligence: {
    label: "Financial Intelligence",
    description: "Advanced P&L analytics, revenue simulations, and employee ROI tracking",
  },
  health_hub: {
    label: "Health Data Hub",
    description: "Aggregate health data from wearables (Fitbit, Garmin, Whoop) for holistic client tracking",
  },
  bulk_messaging: {
    label: "Bulk Messaging",
    description: "Send broadcast messages to multiple clients at once",
  },
};

interface FeatureFlagsContextType {
  hasFeature: (flag: FeatureFlag) => boolean;
  trainerFeatures: Record<string, boolean>;
  loading: boolean;
  refetch: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { organization, loading: orgLoading } = useOrganization();
  const { subscription, loading: subLoading } = useSubscription();
  const [trainerFeatures, setTrainerFeatures] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const fetchTrainerFeatures = useCallback(async () => {
    if (!user) {
      setTrainerFeatures({});
      setLoading(false);
      return;
    }

    try {
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("features")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching trainer features:", error);
        setTrainerFeatures({});
      } else {
        setTrainerFeatures((profile?.features as Record<string, boolean>) || {});
      }
    } catch (error) {
      console.error("Error in fetchTrainerFeatures:", error);
      setTrainerFeatures({});
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    fetchTrainerFeatures();
  }, [fetchTrainerFeatures]);

  // Feature is enabled if tier includes it OR org has it OR trainer has it
  const hasFeature = useCallback((flag: FeatureFlag): boolean => {
    // Check if the user's subscription tier includes this feature
    const tierIncludesFeature = subscription?.tier ? tierHasFeature(subscription.tier, flag) : false;
    // Check if org has manually enabled it
    const orgHasFeature = organization?.settings?.features?.[flag] === true;
    // Check if trainer has manually enabled it
    const trainerHasFeature = trainerFeatures[flag] === true;
    return tierIncludesFeature || orgHasFeature || trainerHasFeature;
  }, [subscription, organization, trainerFeatures]);

  const isLoading = orgLoading || subLoading || loading;

  return (
    <FeatureFlagsContext.Provider
      value={{
        hasFeature,
        trainerFeatures,
        loading: isLoading,
        refetch: fetchTrainerFeatures,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");
  }
  return context;
}
